/*
 * Copyright (c) 2018 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.high_d.axis.group

import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.hierarchy.HierarchyEvent
import com.macrofocus.hierarchy.HierarchyListener
import com.macrofocus.high_d.axis.AxisModel
import com.macrofocus.high_d.axis.hierarchy.AxisHierarchy
import com.macrofocus.order.DefaultVisibleOrder
import com.macrofocus.order.MutableVisibleOrder
import com.macrofocus.order.OrderEvent
import com.macrofocus.order.OrderListener

class FlatAxisGroupModel<R, C>(axisHierarchy: AxisHierarchy<R, C>) : com.macrofocus.high_d.axis.group.AbstractAxisGroupModel<R, C>() {
    private val axisHierarchy: AxisHierarchy<R, C>
    override val axisOrder: DefaultVisibleOrder<AxisModel<R, C>>
    private val collapse: MutableProperty<Boolean> = SimpleProperty<Boolean>(false)
    private val listener: OrderListener<AxisModel<R, C>> = object : OrderListener<AxisModel<R, C>> {
        override fun orderChanged(event: OrderEvent<AxisModel<R, C>>?) {
            update()
        }

        override fun orderVisibility(event: OrderEvent<AxisModel<R, C>>) {
            update()
        }

        override fun orderAdded(event: OrderEvent<AxisModel<R, C>>) {
            update()
        }

        override fun orderRemoved(event: OrderEvent<AxisModel<R, C>>) {
            update()
        }
    }

    private fun update() {
        val all: MutableList<AxisModel<R, C>> = ArrayList<AxisModel<R, C>>()
        val visible: MutableList<AxisModel<R, C>> = ArrayList<AxisModel<R, C>>()
        for (axisGroupModel in axisHierarchy.axisGroupHierarchy.depthFirstIterator()!!) {
            val axisOrder: MutableVisibleOrder<AxisModel<R, C>> = axisGroupModel.axisOrder!!
            for (a in axisOrder.iterableAll()) {
                all.add(a)
            }
            for (v in axisOrder) {
                visible.add(v)
            }
        }
        axisOrder.set(all, visible)
    }

    override val name: String?
        get() = axisHierarchy.axisGroupHierarchy.root.name
    override var isCollapsible: Boolean
        get() = false
        set(collapsible) {}

    override fun collapsedProperty(): MutableProperty<Boolean> {
        return collapsedProperty()
    }

    override var isCollapsed: Boolean
        get() = collapse.value
        set(collapsed) {
            collapse.value = collapsed
        }

    override fun add(axisModel: AxisModel<R, C>) {
        throw UnsupportedOperationException("Not permitted")
    }

    override fun remove(axisModel: AxisModel<R, C>) {
        throw UnsupportedOperationException("Not permitted")
    }

    override fun getAxisModel(name: String?): AxisModel<R, C>? {
        return axisOrder.iterableAll().firstOrNull { it.name == name }
    }

    override val axisModels: Iterable<AxisModel<R, C>>
        get() = axisOrder.iterableAll()

    override val visibleAxisCount: Int
        get() = axisOrder.size()

    init {
        this.axisHierarchy = axisHierarchy
        axisOrder = DefaultVisibleOrder<AxisModel<R, C>>()
        axisHierarchy.axisGroupHierarchy!!.addHierarchyListener(object : HierarchyListener<AxisGroupModel<R, C>> {
            override fun hierarchyNodeInserted(event: HierarchyEvent<AxisGroupModel<R, C>>) {
                event.child!!.axisOrder!!.addOrderListener(listener)
                update()
            }

            override fun hierarchyNodeChanged(event: HierarchyEvent<AxisGroupModel<R, C>>) {
                update()
            }

            override fun hierarchyNodeRemoved(event: HierarchyEvent<AxisGroupModel<R, C>>) {
                event.child!!.axisOrder!!.removeOrderListener(listener)
                update()
            }

            override fun hierarchyStructureChanged(event: HierarchyEvent<AxisGroupModel<R, C>>) {
                update()
            }
        })
        for (axisGroupModel in axisHierarchy.axisGroupHierarchy.depthFirstIterator()) {
            axisGroupModel.axisOrder!!.addOrderListener(listener)
        }
        update()
    }
}